import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { API_BASE_URL } from '../../../config/apiConfig';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  color: #2c3e50;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  
  &:hover {
    color: #2c3e50;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  
  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #495057;
  
  span {
    color: #e74c3c;
  }
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid ${props => props.error ? '#e74c3c' : '#ddd'};
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: ${props => props.error ? '#e74c3c' : '#3498db'};
  }
  
  &::placeholder {
    color: #adb5bd;
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const ErrorText = styled.span`
  font-size: 12px;
  color: #e74c3c;
`;

const SuccessText = styled.span`
  font-size: 12px;
  color: #27ae60;
`;

const PincodeStatus = styled.div`
  font-size: 12px;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &.valid {
    color: #27ae60;
  }
  
  &.invalid {
    color: #e74c3c;
  }
  
  &.loading {
    color: #7f8c8d;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
  border-radius: 0 0 12px 12px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: white;
  border: 1px solid #ddd;
  color: #495057;
  
  &:hover:not(:disabled) {
    background: #f8f9fa;
  }
`;

const SubmitButton = styled(Button)`
  background: #e9fe53;
  border: none;
  color: #202023;
  
  &:hover:not(:disabled) {
    background: #d4e94a;
  }
`;

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
];

export function CustomerModal({ isOpen, onClose, onSave, initialData }) {
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    mobile: '',
    address: '',
    pincode: '',
    city: '',
    state: '',
    gst: '',
    companyName: ''
  });
  const [errors, setErrors] = useState({});
  const [pincodeStatus, setPincodeStatus] = useState({ loading: false, valid: null, message: '' });
  const [saving, setSaving] = useState(false);

  // Initialize form with initial data
  useEffect(() => {
    if (initialData) {
      setFormData({
        firstname: initialData.firstname || '',
        lastname: initialData.lastname || '',
        email: initialData.email || '',
        mobile: initialData.mobile || '',
        address: initialData.address || '',
        pincode: initialData.pincode?.toString() || '',
        city: initialData.city || '',
        state: initialData.state || '',
        gst: initialData.gst || '',
        companyName: initialData.companyName || ''
      });
    } else {
      // Reset form
      setFormData({
        firstname: '',
        lastname: '',
        email: '',
        mobile: '',
        address: '',
        pincode: '',
        city: '',
        state: '',
        gst: '',
        companyName: ''
      });
    }
    setErrors({});
    setPincodeStatus({ loading: false, valid: null, message: '' });
  }, [isOpen, initialData]);

  // Validate pincode with Bluedart API
  useEffect(() => {
    if (formData.pincode.length !== 6) {
      setPincodeStatus({ loading: false, valid: null, message: '' });
      return;
    }

    const validatePincode = async () => {
      setPincodeStatus({ loading: true, valid: null, message: 'Validating with Bluedart...' });
      
      try {
        const token = sessionStorage.getItem('admin-token');
        const response = await fetch(`${API_BASE_URL}/admin/pincode/${formData.pincode}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
          }
        });
        const result = await response.json();
        
        if (result.is_success && result.data.isValid) {
          const serviceableText = result.data.serviceable === true 
            ? '✓ Serviceable' 
            : result.data.serviceable === false 
              ? '✗ Not serviceable' 
              : '';
          
          setPincodeStatus({
            loading: false,
            valid: true,
            message: `${result.data.stateName || result.data.state}${result.data.city ? `, ${result.data.city}` : ''} ${serviceableText}`
          });
          // Auto-fill state and city
          setFormData(prev => ({
            ...prev,
            state: result.data.stateName || result.data.state || prev.state,
            city: result.data.city || prev.city
          }));
        } else {
          setPincodeStatus({
            loading: false,
            valid: false,
            message: result.data?.message || 'Invalid pincode'
          });
        }
      } catch (error) {
        setPincodeStatus({
          loading: false,
          valid: false,
          message: 'Validation failed'
        });
      }
    };

    const timer = setTimeout(validatePincode, 500);
    return () => clearTimeout(timer);
  }, [formData.pincode]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.firstname.trim()) {
      newErrors.firstname = 'First name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    if (formData.pincode && formData.pincode.length !== 6) {
      newErrors.pincode = 'Pincode must be 6 digits';
    }
    
    if (formData.gst && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.gst)) {
      newErrors.gst = 'Invalid GST number format';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setSaving(true);
    
    try {
      const token = sessionStorage.getItem('admin-token');
      const response = await fetch(`${API_BASE_URL}/admin/customers/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify(formData)
      });
      
      const result = await response.json();
      
      if (result.is_success) {
        onSave(result.data.customer);
      } else {
        if (result.existingCustomer) {
          setErrors({ email: 'Customer with this email already exists' });
        } else {
          setErrors({ submit: result.error || 'Failed to create customer' });
        }
      }
    } catch (error) {
      setErrors({ submit: 'Failed to create customer. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Create New Customer</ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>
        
        <form onSubmit={handleSubmit}>
          <ModalBody>
            <FormGrid>
              <FormGroup>
                <Label>First Name <span>*</span></Label>
                <Input
                  type="text"
                  value={formData.firstname}
                  onChange={(e) => handleChange('firstname', e.target.value)}
                  placeholder="Enter first name"
                  error={errors.firstname}
                />
                {errors.firstname && <ErrorText>{errors.firstname}</ErrorText>}
              </FormGroup>
              
              <FormGroup>
                <Label>Last Name</Label>
                <Input
                  type="text"
                  value={formData.lastname}
                  onChange={(e) => handleChange('lastname', e.target.value)}
                  placeholder="Enter last name"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Email <span>*</span></Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="Enter email address"
                  error={errors.email}
                />
                {errors.email && <ErrorText>{errors.email}</ErrorText>}
              </FormGroup>
              
              <FormGroup>
                <Label>Mobile Number</Label>
                <Input
                  type="tel"
                  value={formData.mobile}
                  onChange={(e) => handleChange('mobile', e.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
                  placeholder="10-digit mobile number"
                />
              </FormGroup>
              
              <FormGroup className="full-width">
                <Label>Address</Label>
                <Input
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="Street address"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Pincode</Label>
                <Input
                  type="text"
                  value={formData.pincode}
                  onChange={(e) => handleChange('pincode', e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                  placeholder="6-digit pincode"
                  error={errors.pincode}
                />
                {errors.pincode && <ErrorText>{errors.pincode}</ErrorText>}
                {pincodeStatus.message && (
                  <PincodeStatus className={pincodeStatus.loading ? 'loading' : pincodeStatus.valid ? 'valid' : 'invalid'}>
                    {pincodeStatus.loading ? '⏳' : pincodeStatus.valid ? '✓' : '✗'} {pincodeStatus.message}
                  </PincodeStatus>
                )}
              </FormGroup>
              
              <FormGroup>
                <Label>City</Label>
                <Input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="City"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>State</Label>
                <Select
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                >
                  <option value="">Select State</option>
                  {INDIAN_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label>GST Number (Optional)</Label>
                <Input
                  type="text"
                  value={formData.gst}
                  onChange={(e) => handleChange('gst', e.target.value.toUpperCase())}
                  placeholder="e.g., 30AAAAA0000A1Z5"
                  error={errors.gst}
                />
                {errors.gst && <ErrorText>{errors.gst}</ErrorText>}
              </FormGroup>
              
              {formData.gst && formData.gst.length > 0 && (
                <FormGroup>
                  <Label>Company Name</Label>
                  <Input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => handleChange('companyName', e.target.value)}
                    placeholder="Company / Business name"
                  />
                </FormGroup>
              )}
            </FormGrid>
            
            {errors.submit && (
              <ErrorText style={{ display: 'block', marginTop: '16px', textAlign: 'center' }}>
                {errors.submit}
              </ErrorText>
            )}
          </ModalBody>
          
          <ModalFooter>
            <CancelButton type="button" onClick={onClose}>
              Cancel
            </CancelButton>
            <SubmitButton type="submit" disabled={saving}>
              {saving ? 'Creating...' : 'Create Customer'}
            </SubmitButton>
          </ModalFooter>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
}
